<!--
  - Author: Raul Perusquia <raul@inikoo.com>
  - Created: Wed, 10 May 2023 09:18:00 Malaysia Time, Pantai Lembeng, Bali, Id
  - Copyright (c) 2023, Raul A Perusquia Flores
  -->

<script setup lang="ts">
import Multiselect from "@vueform/multiselect"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faExclamationCircle, faCheckCircle } from '@fas'
import { library } from "@fortawesome/fontawesome-svg-core"
import { computed, onMounted, ref } from "vue"
import { routeType } from '@/types/route'
import PureMultiselectInfiniteScroll from '@/Components/Pure/PureMultiselectInfiniteScroll.vue';
import Button from '@/Components/Utils/Button.vue'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { requestCopyFromCustomer } from '@/Composables/useCopyFromCustomer'


library.add(faExclamationCircle, faCheckCircle)

const props = defineProps<{
    form: any
    fieldName: any
    options: string[] | {label?: string, value: string}[]
    fieldData: {
        placeholder?: string
        required?: boolean
        mode?: "multiple" | "single" | "tags"
		searchable?: boolean
        readonly?: boolean
		labelProp?: string
		fetchRoute: routeType
		valueProp?: string
		canCopyFromCustomer?: boolean
		copyFromCustomerLabel?: string
		start_value?: {
			customer: any[]
		}
    }
}>()

// Auto assign to first option if 'required' and value is null
onMounted(() => {
    if(props.fieldData?.required && !props.form[props.fieldName]) {
        props.form[props.fieldName] = props.options?.[0]?.value
    }
})

// The picked customer, so its data can be copied into the sibling fields
const knownCustomers = ref<any[]>(props.fieldData?.start_value?.customer ?? [])
const selectedCustomer = computed(
    () => knownCustomers.value.find((customer: any) => customer?.id === props.form[props.fieldName])
)

const onOptionsList = (list: any[]) => knownCustomers.value = list
const onSelectedOption = (customer: any) => {
    if (customer && !knownCustomers.value.some((known: any) => known?.id === customer.id)) {
        knownCustomers.value = [...knownCustomers.value, customer]
    }
}
</script>

<template>
	<div class="">
		<div class="relative"
            :class="form.errors[fieldName] ? 'errorShake' : ''"
        >
			<!-- <Multiselect
				v-model="form[fieldName]"
                @update:modelValue="() => form.errors[fieldName] = null"
				:class="{ 'pr-8': form.errors[fieldName] || form.recentlySuccessful }"
				:options="props.options"
				:placeholder="props.fieldData.placeholder ?? 'Select your option'"
				:canClear="!props.fieldData.required"
				:mode="props.fieldData.mode ? props.fieldData.mode : 'single'"
				:closeOnSelect="props.fieldData.mode == 'multiple' ? false : true"
				:canDeselect="!props.fieldData.required"
				:hideSelected="false"
                :disabled="fieldData.readonly"
                :caret="!fieldData.readonly"
				:searchable="!!props.fieldData.searchable" 
				:label="fieldData.labelProp || 'label'"
				:valueProp="fieldData.valueProp || 'value'"
			/> -->

			<PureMultiselectInfiniteScroll
				v-model="form[fieldName]"
				@update:modelValue="() => form.errors[fieldName] = null"
				@optionsList="onOptionsList"
				@selectedOption="onSelectedOption"
				:fetchRoute="fieldData.fetchRoute"
				:options="fieldData?.start_value?.customer"
				labelProp="name"
				:required="fieldData.required"
				placeholder="Pilih customer"
				:errorLabel="{
					title: 'Oops! Ada kesalahan',
					text: 'Gagal mengambil data customer. Silahkan coba lagi.'
				}"
			>
				<template #singlelabel="{ value }">
					<div class="w-full text-left pl-4">{{ value.name }} <span class="text-sm text-gray-400">({{ useFormatNumber(value.orders_count || 0) }} order)</span></div>
				</template>

				<template #option="{ option, isSelected, isPointed }">
					<div class="">{{ option.name }} <span class="text-sm" :class="isSelected(option) ? 'text-indigo-200' : 'text-gray-400'">({{ useFormatNumber(option.orders_count || 0) }} order)</span></div>
				</template>
			</PureMultiselectInfiniteScroll>


			<div
				v-if="form.errors[fieldName] || form.recentlySuccessful"
				class="absolute inset-y-2/4 right-0 pr-3 flex items-center pointer-events-none bg-red-500">
				<FontAwesomeIcon
					icon="fas fa-exclamation-circle"
					v-if="form.errors[fieldName]"
					class="h-5 w-5 text-red-500"
					aria-hidden="true" />
				<FontAwesomeIcon
					icon="fas fa-check-circle"
					v-if="form.recentlySuccessful"
					class="mt-1.5 h-5 w-5 text-green-500"
					aria-hidden="true" />
			</div>
		</div>
        
		<p v-if="form.errors[fieldName]" class="mt-2 text-sm text-red-600" id="email-error">
			{{ form.errors[fieldName] }}
		</p>

		<!-- Autofill: only once a customer is picked, and only fills the fields (still editable) -->
		<div v-if="fieldData.canCopyFromCustomer && selectedCustomer" class="mt-2">
			<Button
				size="xs"
				:style="'dashed'"
				icon="fal fa-clipboard"
				:label="fieldData.copyFromCustomerLabel || 'Samakan datanya dengan pelanggan'"
				:tooltip="`Isi otomatis dari data ${selectedCustomer.name}. Bisa diubah sebelum disimpan.`"
				@click="() => requestCopyFromCustomer(selectedCustomer)"
			/>
		</div>
	</div>
</template>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
/* Style for multiselect globally */
:deep(.multiselect-option.is-selected),
:deep(.multiselect-option.is-selected.is-pointed) {
	background: var(--ms-option-bg-selected, #6366f1) !important;
	color: var(--ms-option-color-selected, #fff) !important;
}

:deep(.multiselect-option.is-selected.is-disabled) {
	background: var(--ms-option-bg-selected-disabled, #c7d2fe);
	color: var(--ms-option-color-selected-disabled, #818cf8);
}

:deep(.multiselect.is-active) {
	border: var(--ms-border-width-active, var(--ms-border-width, 1px)) solid
		var(--ms-border-color-active, var(--ms-border-color, #d1d5db));
	box-shadow: 0 0 0 var(--ms-ring-width, 3px) var(--ms-ring-color, rgba(99, 102, 241, 0.188));
}
</style>
